"use client";

import React from "react";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { Calendar, Users } from "lucide-react";
import { Button } from "../button.js";
import { Input } from "../input.js";
import { Textarea } from "../textarea.js";
import { Badge } from "../badge.js";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../form.js";
import { zodResolver } from "../../forms/safe-zod-resolver.js";
import { useToast } from "../../hooks/use-toast.js";
import { CalendarEvent } from "../../types/calendar.js";
import {
  useUpdateGoogleEvent,
  useUpdateMicrosoftEvent,
} from "../../hooks/use-event-mutations.js";
import { ResponsiveModal } from "../responsive-modal.js";
import { AttendeeChips } from "./attendees/AttendeeChips.js";
import { AttendeeSelectWithModal } from "./attendees/AttendeeSelectWithModal.js";
import { useCalendarContext } from "../../lib/calendar-context.js";
import {
  EventFormGrid,
  EventFormPrimaryColumn,
  EventFormSidebar,
  EventFormSection,
} from "./calendarinvites/shared/EventFormShell.js";
import { RHFDateTimeFields } from "./calendarinvites/shared/RHFDateTimeFields.js";
import {
  EventEditSchema,
  type EventEditFormValues,
} from "./calendarinvites/validation.js";

interface EventEditDialogProps {
  event: CalendarEvent | null;
  isOpen: boolean;
  onClose: () => void;
  // Optional props for advanced contact search integration
  contacts?: Array<{ id: string; name: string; email: string; company_name?: string }>;
  onSearchContacts?: (searchTerm: string) => Promise<Array<{ id: string; name: string; email: string; company_name?: string }>>;
  isLoadingContacts?: boolean;
}

/**
 * Convert an RHF `Date` to the local wall-clock string the calendar update APIs
 * expect for `eventDetails.start` / `eventDetails.end`. The API pairs this value
 * with a separate `timeZone` field and interprets it as a zone-less local time,
 * so we MUST send a local datetime string (no `Z`) — sending `.toISOString()`
 * (UTC) would shift the event by the timezone offset. This preserves the exact
 * save behavior of the original `datetime-local` string inputs.
 */
function toLocalDateTimeString(date: Date): string {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
}

export function EventEditDialog({
  event,
  isOpen,
  onClose,
  contacts: propContacts,
  onSearchContacts: propOnSearchContacts,
  isLoadingContacts: propIsLoadingContacts,
}: EventEditDialogProps) {
  // Get contact search functionality from CalendarContext
  const calendarContext = useCalendarContext();
  const contacts = propContacts || calendarContext.contacts;
  const onSearchContacts = propOnSearchContacts || calendarContext.onSearchContacts;
  const isLoadingContacts = propIsLoadingContacts || calendarContext.isLoadingContacts;
  const { toast } = useToast();
  const [isSaving, setIsSaving] = React.useState(false);

  const updateGoogleEvent = useUpdateGoogleEvent();
  const updateMicrosoftEvent = useUpdateMicrosoftEvent();

  // Detect provider by event shape (source can be an email, not provider name)
  const isMicrosoftEvent = Boolean(
    (event as any)?.["@odata.etag"] || (event as any)?.odataEtag,
  );
  const isGoogleEvent = !isMicrosoftEvent;

  const form = useForm<EventEditFormValues>({
    resolver: zodResolver(EventEditSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      title: "",
      description: "",
      location: "",
      startDateTime: undefined,
      endDateTime: undefined,
    },
  });

  // Initialize the form when the dialog opens. Convert the event's ISO start/end
  // strings to Date objects for the RHF datetime fields.
  React.useEffect(() => {
    if (event && isOpen) {
      form.reset({
        title: event.title ?? "",
        description: event.description ?? "",
        location: event.location ?? "",
        startDateTime: event.start ? new Date(event.start) : undefined,
        endDateTime: event.end ? new Date(event.end) : undefined,
      });
    }
    // form is stable from react-hook-form; intentionally keyed on event + isOpen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, isOpen]);

  // Attendees aggregation from event data if available
  const { existingEmails, attendeeNames } = React.useMemo(() => {
    const emails: string[] = [];
    const names: Record<string, string> = {};

    if (event?.attendees && Array.isArray(event.attendees)) {
      for (const a of event.attendees as any[]) {
        if (typeof a === "string") {
          emails.push(a);
        } else if (a && typeof a.email === "string") {
          emails.push(a.email);
          if (a.name && typeof a.name === "string") {
            names[a.email] = a.name;
          }
        }
      }
    }

    const org: any = (event as any)?.organizer;
    if (org) {
      if (typeof org === "string") {
        emails.push(org);
      } else if (typeof org.email === "string") {
        emails.push(org.email);
        if (org.name && typeof org.name === "string") {
          names[org.email] = org.name;
        }
      }
    }

    return {
      existingEmails: Array.from(new Set(emails)),
      attendeeNames: names,
    };
  }, [event]);

  // Attendees stay OUTSIDE react-hook-form (not part of EventEditSchema).
  const [attendeeEmails, setAttendeeEmails] = React.useState<string[]>(existingEmails);
  React.useEffect(() => setAttendeeEmails(existingEmails), [existingEmails]);

  const onValid = async (values: EventEditFormValues) => {
    if (!event?.id) return;

    setIsSaving(true);

    try {
      // Build attendees list from selected emails
      const attendees = attendeeEmails.map((email) => ({ email }));

      const start = toLocalDateTimeString(values.startDateTime);
      const end = toLocalDateTimeString(values.endDateTime);
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (isGoogleEvent) {
        const eventData = {
          summary: values.title,
          description: values.description,
          location: values.location,
          start,
          end,
          allDay: event.allDay,
          timeZone,
          attendees,
        } as any;

        await updateGoogleEvent.mutateAsync({
          eventId: event.id,
          eventDetails: eventData,
        });
      } else if (isMicrosoftEvent) {
        // Microsoft uses different field names
        const msftEventData = {
          subject: values.title,
          body: {
            content: values.description,
            contentType: "text",
          },
          location: {
            displayName: values.location,
          },
          start,
          end,
          timeZone,
          attendees,
        } as any;

        await updateMicrosoftEvent.mutateAsync({
          eventId: event.id,
          eventDetails: msftEventData,
        });
      }

      onClose();
    } catch (error) {
      console.error("Failed to update event:", error);
      toast({
        title: "Error",
        description: "Failed to update event. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !event) return null;

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={onClose}
      title="Edit Event"
      description="Modify event details and save changes"
      maxWidth="4xl"
      side="bottom"
      showCloseButton={true}
    >
      {/* Desktop: Custom header with badge */}
      <div className="md:flex md:items-center md:justify-between md:gap-3 md:mb-6 hidden">
        <div className="flex items-center space-x-3">
          <h2 className="text-xl font-semibold">Edit Event</h2>
          <Badge className="bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
            {event.source}
          </Badge>
        </div>
      </div>

      {/* Mobile: Enhanced header integrated into drawer header */}
      <div className="md:hidden mb-4">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold">Edit Event</h3>
          <Badge className="bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
            {event.source}
          </Badge>
        </div>
      </div>

      {/* Form Content - Scrollable container */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onValid)} className="space-y-6">
            <EventFormGrid>
              {/* Left: primary inputs */}
              <EventFormPrimaryColumn>
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Event Title</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter event title"
                          disabled={isSaving}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          value={field.value ?? ""}
                          placeholder="Enter event description"
                          rows={6}
                          disabled={isSaving}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          placeholder="Enter event location"
                          disabled={isSaving}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </EventFormPrimaryColumn>

              {/* Right: date/time, attendees, read-only facts */}
              <EventFormSidebar>
                <EventFormSection title="When" icon={Calendar}>
                  <RHFDateTimeFields
                    control={form.control}
                    startName="startDateTime"
                    endName="endDateTime"
                    startLabel="Start Date & Time"
                    endLabel="End Date & Time"
                    stacked
                    disabled={isSaving}
                  />
                </EventFormSection>

                <EventFormSection title="Attendees" icon={Users}>
                  <AttendeeSelectWithModal
                    currentEmails={attendeeEmails}
                    onChange={setAttendeeEmails}
                    disabled={isSaving}
                    placeholder="Search contacts or enter email addresses..."
                    contacts={contacts}
                    onSearchContacts={onSearchContacts}
                    isLoadingContacts={isLoadingContacts}
                  />
                  {attendeeEmails.length > 0 && (
                    <AttendeeChips
                      emails={attendeeEmails}
                      names={attendeeNames}
                      clickable={false}
                    />
                  )}
                </EventFormSection>

                <EventFormSection title="Event Details">
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Source:</span>
                      <span className="font-medium capitalize">{event.source}</span>
                    </div>
                    {event.attendees && event.attendees.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Attendees:</span>
                        <span className="font-medium">{event.attendees.length}</span>
                      </div>
                    )}
                  </div>
                </EventFormSection>
              </EventFormSidebar>
            </EventFormGrid>

            {/* Action buttons footer */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </ResponsiveModal>
  );
}
